import {
  useVaultDocuments,
  useVault,
  useVaultDocumentPeriods,
  vaultKeys,
} from '@/hooks/useVault';
import { useVaultKeyStore } from '@/store/vaultKeyStore';
import {
  DocumentType,
  VaultDocument,
  VaultEncryptionMode,
} from '@/types/vault.types';
import {
  decryptDocumentComplete,
  decryptDocumentWithRSA,
} from '@/utils/crypto/vaultCrypto';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Download,
  Eye,
  FileText,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Send,
  X,
} from 'lucide-react';
import { useState, useCallback, useMemo } from 'react';
import { MigrateDocumentModal } from './MigrateDocumentModal';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { vaultSyncConfigurationService } from '@/services/vault-sync-configuration.service';
import { useQueryClient } from '@tanstack/react-query';

import { APP_CONFIG } from '@/constants/app';
import { TwoFactorRequiredPrompt } from './TwoFactorRequiredPrompt';

interface DocumentsTabProps {
  vaultId: string;
}

export const DocumentsTab = ({ vaultId }: DocumentsTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [periodFilter, setPeriodFilter] = useState<string>('all'); // format: "YYYY-MM" or "all"
  const [requires2fa, setRequires2fa] = useState(false);

  // Migration modal state
  const [migrateModalOpen, setMigrateModalOpen] = useState(false);
  const [documentsToMigrate, setDocumentsToMigrate] = useState<VaultDocument[]>(
    []
  );

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<VaultDocument | null>(
    null
  );
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Transfer to employee state
  const [transferringDocId, setTransferringDocId] = useState<string | null>(
    null
  );
  const [bulkTransferring, setBulkTransferring] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  // Parse period filter to year/month
  const periodParams =
    periodFilter !== 'all'
      ? {
          year: parseInt(periodFilter.split('-')[0], 10),
          month: parseInt(periodFilter.split('-')[1], 10),
        }
      : {};

  const {
    data: documentsResponse,
    isLoading,
    error,
  } = useVaultDocuments(
    vaultId,
    true,
    Object.keys(periodParams).length > 0 ? periodParams : undefined
  );
  const { data: availablePeriods } = useVaultDocumentPeriods(vaultId);
  const { data: vaultData } = useVault(vaultId);
  const getMasterKey = useVaultKeyStore(state => state.getMasterKey);
  const isUnlocked = useVaultKeyStore(state =>
    state.unlockedVaults.has(vaultId)
  );

  // Check if error is due to 2FA requirement.
  // ⚠️ Ne PAS faire de `return` ici : des hooks (useCallback) sont déclarés
  // plus bas. Un retour anticipé rend moins de hooks au rendu suivant et
  // provoque le crash React « rendered fewer hooks than expected » (écran de
  // secours). On mémorise juste l'état et on rend le prompt après tous les hooks.
  const is2faRequired =
    (error as HttpApiError)?.response?.data?.error === 'TWO_FACTOR_REQUIRED' ||
    (error as HttpApiError)?.response?.data?.twoFactorRequired === true;
  const show2faPrompt = is2faRequired || requires2fa;

  const documents = useMemo(() => documentsResponse || [], [documentsResponse]);

  // Filtrage par recherche côté client (le filtrage par période est fait côté serveur)
  const filteredDocuments = useMemo(
    () =>
      documents.filter((doc: VaultDocument) => {
        return (
          searchQuery === '' ||
          doc.originalFilename.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }),
    [documents, searchQuery]
  );

  // Formater le label d'une période (ex: "Janvier 2025")
  const formatPeriodLabel = (year: number, month: number): string => {
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const handleDownload = async (document: VaultDocument) => {
    try {
      const result = await fetchAndDecryptDocument(document);
      if (result) {
        const url = URL.createObjectURL(result.blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = result.filename;
        window.document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
        toast.success(
          t(
            'vault.messages.downloadSuccess',
            'Document téléchargé et déchiffré'
          )
        );
      }
    } catch (error) {
      console.error('[DocumentsTab] Failed to download document:', error);
      toast.error(
        t(
          'vault.messages.downloadError',
          'Erreur lors du téléchargement ou du déchiffrement'
        )
      );
    }
  };

  const isServerSide =
    vaultData?.encryptionMode === VaultEncryptionMode.SERVER_SIDE;

  // Detect legacy documents (ZERO_KNOWLEDGE encrypted in a SERVER_SIDE vault)
  const isLegacyDocument = useCallback(
    (doc: VaultDocument): boolean =>
      isServerSide && doc.metadata?.encryptionMode !== 'SERVER_SIDE',
    [isServerSide]
  );

  const legacyDocuments = documents.filter(isLegacyDocument);
  const hasLegacyDocuments = legacyDocuments.length > 0;

  const handleMigrateSingle = (doc: VaultDocument) => {
    setDocumentsToMigrate([doc]);
    setMigrateModalOpen(true);
  };

  const handleMigrateAll = () => {
    setDocumentsToMigrate(legacyDocuments);
    setMigrateModalOpen(true);
  };

  /**
   * Télécharge un document SERVER_SIDE (le serveur retourne le contenu déchiffré)
   */
  const fetchServerSideDocument = async (
    document: VaultDocument
  ): Promise<{ blob: Blob; filename: string } | null> => {
    const downloadUrl = `${APP_CONFIG.api.baseUrl}/vaults/${vaultId}/documents/${document.uuid}/download`;

    const response = await fetch(downloadUrl, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        if (
          errorJson.error === 'TWO_FACTOR_REQUIRED' ||
          errorJson.twoFactorRequired
        ) {
          setRequires2fa(true);
          toast.error(
            t(
              'vault.messages.twoFactorRequired',
              "L'authentification \u00e0 deux facteurs est requise pour acc\u00e9der aux documents"
            )
          );
          return null;
        }
      } catch {
        // Not JSON
      }
      throw new Error(`Failed to download document: ${response.status}`);
    }

    const originalFilename =
      response.headers.get('X-Original-Filename') || document.originalFilename;
    const content = await response.arrayBuffer();
    const blob = new Blob([content], {
      type: document.mimeType || 'application/octet-stream',
    });
    return { blob, filename: originalFilename };
  };

  /**
   * Télécharge, déchiffre et retourne le contenu déchiffré
   */
  const fetchAndDecryptDocument = async (
    document: VaultDocument
  ): Promise<{ blob: Blob; filename: string } | null> => {
    // SERVER_SIDE: le serveur gère le déchiffrement
    if (isServerSide) {
      return fetchServerSideDocument(document);
    }

    // ZERO_KNOWLEDGE: déchiffrement côté client
    // 1. Check if vault is unlocked
    if (!isUnlocked) {
      toast.error(
        t(
          'vault.messages.vaultLocked',
          'Le coffre est verrouillé. Veuillez le déverrouiller pour accéder aux documents.'
        )
      );
      return null;
    }

    // 2. Get vault master key from memory
    const masterKey = getMasterKey(vaultId);
    if (!masterKey) {
      toast.error(
        t('vault.messages.noVaultKey', 'Clé du coffre non disponible')
      );
      return null;
    }

    // 3. Check if document has encryption metadata
    if (!document.iv || !document.authTag) {
      toast.error(
        t(
          'vault.messages.noEncryptionMetadata',
          'Métadonnées de chiffrement manquantes'
        )
      );
      return null;
    }

    const downloadUrl = `${APP_CONFIG.api.baseUrl}/vaults/${vaultId}/documents/${document.uuid}/download`;

    // 4. Download encrypted document from server
    const response = await fetch(downloadUrl, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        if (
          errorJson.error === 'TWO_FACTOR_REQUIRED' ||
          errorJson.twoFactorRequired
        ) {
          setRequires2fa(true);
          toast.error(
            t(
              'vault.messages.twoFactorRequired',
              "L'authentification \u00e0 deux facteurs est requise pour acc\u00e9der aux documents"
            )
          );
          return null;
        }
      } catch {
        // Not JSON
      }
      throw new Error(`Failed to download document: ${response.status}`);
    }

    // 5. Extract encryption metadata from response headers
    const encryptedDocumentKey = response.headers.get('X-Encryption-Key');
    const iv = response.headers.get('X-Encryption-IV');
    const authTag = response.headers.get('X-Encryption-Auth-Tag');
    const documentKeyIv = response.headers.get('X-Document-Key-IV');
    const documentKeyAuthTag = response.headers.get('X-Document-Key-Auth-Tag');
    const originalFilename =
      response.headers.get('X-Original-Filename') || document.originalFilename;

    if (!encryptedDocumentKey || !iv || !authTag) {
      throw new Error('Missing encryption metadata in response headers');
    }

    // 6. Get encrypted content as ArrayBuffer
    const encryptedContent = await response.arrayBuffer();

    // 7. Decrypt document client-side
    const isRSAEncrypted = !documentKeyIv || !documentKeyAuthTag;
    let decrypted: ArrayBuffer;

    if (isRSAEncrypted) {
      if (!vaultData?.encryptedPrivateKey) {
        toast.error(
          t(
            'vault.messages.migrationRequired',
            'Ce document nécessite une migration du coffre. Veuillez verrouiller puis déverrouiller le coffre pour lancer la migration.'
          )
        );
        return null;
      }

      decrypted = await decryptDocumentWithRSA(
        encryptedContent,
        encryptedDocumentKey,
        iv,
        authTag,
        vaultData.encryptedPrivateKey,
        masterKey
      );
    } else {
      decrypted = await decryptDocumentComplete(
        encryptedContent,
        encryptedDocumentKey,
        iv,
        authTag,
        documentKeyIv,
        documentKeyAuthTag,
        masterKey
      );
    }

    const blob = new Blob([decrypted], {
      type: document.mimeType || 'application/octet-stream',
    });
    return { blob, filename: originalFilename };
  };

  /**
   * Visualise un document dans une modale
   */
  const handlePreview = async (document: VaultDocument) => {
    setPreviewDocument(document);
    setPreviewLoading(true);
    setPreviewOpen(true);

    try {
      const result = await fetchAndDecryptDocument(document);
      if (result) {
        const url = URL.createObjectURL(result.blob);
        setPreviewContent(url);
      } else {
        setPreviewOpen(false);
      }
    } catch (error) {
      console.error('[DocumentsTab] Failed to preview document:', error);
      toast.error(
        t(
          'vault.messages.previewError',
          'Erreur lors de la prévisualisation du document'
        )
      );
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  /**
   * Ferme la modale de prévisualisation
   */
  const closePreview = () => {
    if (previewContent) {
      URL.revokeObjectURL(previewContent);
    }
    setPreviewOpen(false);
    setPreviewDocument(null);
    setPreviewContent(null);
  };

  // Transfer handlers
  const handleTransferToEmployee = useCallback(
    async (documentUuid: string) => {
      setTransferringDocId(documentUuid);
      try {
        const result =
          await vaultSyncConfigurationService.transferToEmployee(documentUuid);
        if (result.success) {
          toast.success(
            t(
              'vault.transfer.success',
              '{{count}} bulletin(s) transféré(s) aux coffres employés',
              {
                count: result.transferredCount,
              }
            )
          );
          queryClient.invalidateQueries({
            queryKey: vaultKeys.documents(vaultId),
          });
        } else {
          toast.error(
            t('vault.transfer.error', 'Erreur lors du transfert : {{errors}}', {
              errors: result.errors.join(', '),
            })
          );
        }
      } catch (cause) {
        const error = cause as HttpApiError;
        console.error('[DocumentsTab] Transfer failed:', error);
        toast.error(
          t(
            'vault.transfer.errorGeneric',
            'Erreur lors du transfert vers les coffres employés'
          )
        );
      } finally {
        setTransferringDocId(null);
      }
    },
    [vaultId, queryClient, t]
  );

  const handleBulkTransfer = useCallback(async () => {
    if (selectedDocIds.size === 0) return;
    setBulkTransferring(true);
    try {
      const result = await vaultSyncConfigurationService.bulkTransferToEmployee(
        {
          documentUuids: Array.from(selectedDocIds),
        }
      );
      if (result.success) {
        toast.success(
          t(
            'vault.transfer.bulkSuccess',
            '{{count}} bulletin(s) transféré(s) aux coffres employés',
            {
              count: result.totalTransferred,
            }
          )
        );
        setSelectedDocIds(new Set());
        queryClient.invalidateQueries({
          queryKey: vaultKeys.documents(vaultId),
        });
      } else {
        toast.error(
          t('vault.transfer.error', 'Erreur lors du transfert : {{errors}}', {
            errors: result.errors.join(', '),
          })
        );
      }
    } catch (cause) {
      const error = cause as HttpApiError;
      console.error('[DocumentsTab] Bulk transfer failed:', error);
      toast.error(
        t(
          'vault.transfer.errorGeneric',
          'Erreur lors du transfert vers les coffres employés'
        )
      );
    } finally {
      setBulkTransferring(false);
    }
  }, [selectedDocIds, vaultId, queryClient, t]);

  const toggleDocSelection = useCallback((docId: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  }, []);

  // Get documents eligible for transfer (payslips in folder vault not yet transferred)
  const untransferredPayslips = useMemo(
    () =>
      isServerSide
        ? filteredDocuments.filter(
            (doc: VaultDocument) =>
              doc.type === DocumentType.PAYSLIP &&
              !doc.transferredToEmployeeVault &&
              !isLegacyDocument(doc)
          )
        : [],
    [filteredDocuments, isLegacyDocument, isServerSide]
  );

  const selectAllUntransferred = useCallback(() => {
    setSelectedDocIds(
      new Set(untransferredPayslips.map((d: VaultDocument) => d.uuid))
    );
  }, [untransferredPayslips]);

  const deselectAll = useCallback(() => {
    setSelectedDocIds(new Set());
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDocumentTypeLabel = (type: DocumentType): string => {
    const labels = {
      [DocumentType.PAYSLIP]: t(
        'vault.documentType.payslip',
        'Bulletin de paie'
      ),
      [DocumentType.DSN]: t('vault.documentType.dsn', 'DSN'),
      [DocumentType.EDITION]: t('vault.documentType.edition', 'Édition'),
      [DocumentType.CONTRACT]: t('vault.documentType.contract', 'Contrat'),
      [DocumentType.OTHER]: t('vault.documentType.other', 'Autre'),
    };
    return labels[type] || type;
  };

  const getDocumentTypeColor = (type: DocumentType): string => {
    const colors = {
      [DocumentType.PAYSLIP]: 'bg-blue-100 text-blue-700',
      [DocumentType.DSN]: 'bg-purple-100 text-purple-700',
      [DocumentType.EDITION]: 'bg-green-100 text-green-700',
      [DocumentType.CONTRACT]: 'bg-orange-100 text-orange-700',
      [DocumentType.OTHER]: 'bg-neutral-100 text-neutral-700',
    };
    return colors[type] || 'bg-neutral-100 text-neutral-700';
  };

  // Retours tardifs (après TOUS les hooks) — sûrs vis-à-vis des règles des hooks.
  if (show2faPrompt) {
    return (
      <TwoFactorRequiredPrompt
        onSetup={() => navigate('/settings?tab=security')}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"></div>
      </div>
    );
  }

  // Erreur de chargement (hors 2FA) : afficher une VRAIE erreur, jamais un faux
  // "coffre vide" — sinon le salarié croit ses bulletins disparus alors que
  // l'API est en échec.
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-base font-semibold text-red-800">
          {t(
            'vault.messages.documentsLoadError',
            'Impossible de charger les documents'
          )}
        </p>
        <p className="mt-1 text-sm text-red-600">
          {t(
            'vault.messages.documentsLoadErrorHint',
            'Une erreur est survenue. Vos documents ne sont pas perdus — veuillez réessayer dans un instant.'
          )}
        </p>
        <button
          type="button"
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: vaultKeys.documents(vaultId),
            })
          }
          className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
        >
          {t('common.retry', 'Réessayer')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Lock Status Warning - Not shown for SERVER_SIDE vaults */}
      {!isServerSide && !isUnlocked && documents.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-5">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
              <Lock className="h-6 w-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-semibold text-amber-900">
                {t('vault.messages.vaultLockedWarning', 'Coffre verrouillé')}
              </h4>
              <p className="mt-1 text-sm text-amber-700">
                {t(
                  'vault.messages.unlockToDownload',
                  'Pour télécharger vos documents, cliquez sur le bouton "Déverrouiller" en haut de la page.'
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        {/* Search */}
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-5 w-5 text-neutral-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t(
              'vault.searchDocuments',
              'Rechercher un document...'
            )}
            className="focus:border-primary-500 focus:ring-primary-100 block w-full rounded-lg border border-neutral-300 py-2 pr-3 pl-10 text-sm shadow-sm transition-colors focus:ring-2 focus:outline-none"
          />
        </div>

        {/* Period Filter */}
        <select
          value={periodFilter}
          onChange={e => setPeriodFilter(e.target.value)}
          className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-4 py-2 text-sm shadow-sm transition-colors focus:ring-2 focus:outline-none"
        >
          <option value="all">
            {t('vault.filter.allPeriods', 'Toutes les périodes')}
          </option>
          {availablePeriods?.map(period => (
            <option
              key={`${period.year}-${period.month}`}
              value={`${period.year}-${period.month}`}
            >
              {formatPeriodLabel(period.year, period.month)}
            </option>
          ))}
        </select>
      </div>

      {/* Legacy Documents Banner */}
      {hasLegacyDocuments && (
        <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-amber-900">
                  {t(
                    'vault.migrate.legacyBanner',
                    '{{count}} document(s) en chiffrement legacy',
                    {
                      count: legacyDocuments.length,
                    }
                  )}
                </h4>
                <p className="text-xs text-amber-700">
                  {t(
                    'vault.migrate.legacyBannerDesc',
                    'Ces documents doivent \u00eatre migr\u00e9s pour \u00eatre accessibles sans mot de passe'
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={handleMigrateAll}
              className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
            >
              <RefreshCw className="h-4 w-4" />
              {t('vault.migrate.migrateAll', 'Migrer tous')}
            </button>
          </div>
        </div>
      )}

      {/* Bulk Transfer Bar */}
      {isServerSide && untransferredPayslips.length > 0 && (
        <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                <Send className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-emerald-900">
                  {t(
                    'vault.transfer.bulkTitle',
                    '{{count}} bulletin(s) en attente de transfert',
                    {
                      count: untransferredPayslips.length,
                    }
                  )}
                </h4>
                <p className="text-xs text-emerald-700">
                  {selectedDocIds.size > 0
                    ? t(
                        'vault.transfer.selectedCount',
                        '{{count}} sélectionné(s)',
                        {
                          count: selectedDocIds.size,
                        }
                      )
                    : t(
                        'vault.transfer.bulkDesc',
                        'Sélectionnez les bulletins à transférer vers les coffres employés'
                      )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedDocIds.size === 0 ? (
                <button
                  onClick={selectAllUntransferred}
                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-50"
                >
                  {t('vault.transfer.selectAll', 'Tout sélectionner')}
                </button>
              ) : (
                <>
                  <button
                    onClick={deselectAll}
                    className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                  >
                    {t('vault.transfer.deselectAll', 'Désélectionner')}
                  </button>
                  <button
                    onClick={handleBulkTransfer}
                    disabled={bulkTransferring}
                    className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {bulkTransferring ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {t(
                      'vault.transfer.bulkTransfer',
                      'Transférer la sélection'
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {filteredDocuments.length > 0 ? (
        <div className="rounded-lg border border-neutral-200 bg-white">
          <div className="overflow-x-auto rounded-lg">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  {isServerSide && untransferredPayslips.length > 0 && (
                    <th className="w-10 px-3 py-3">
                      <span className="sr-only">
                        {t('vault.transfer.select', 'Sélectionner')}
                      </span>
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-neutral-500 uppercase">
                    {t('vault.table.document', 'Document')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-neutral-500 uppercase">
                    {t('vault.table.type', 'Type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-neutral-500 uppercase">
                    {t('vault.table.size', 'Taille')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-neutral-500 uppercase">
                    {t('vault.table.uploadedAt', 'Date')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-neutral-500 uppercase">
                    {t('vault.table.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 bg-white">
                {filteredDocuments.map((document: VaultDocument) => (
                  <tr
                    key={document.uuid}
                    className="transition-colors hover:bg-neutral-50"
                  >
                    {isServerSide && untransferredPayslips.length > 0 && (
                      <td className="w-10 px-3 py-4">
                        {document.type === DocumentType.PAYSLIP &&
                          !document.transferredToEmployeeVault &&
                          !isLegacyDocument(document) && (
                            <input
                              type="checkbox"
                              checked={selectedDocIds.has(document.uuid)}
                              onChange={() => toggleDocSelection(document.uuid)}
                              className="h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
                            />
                          )}
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-neutral-400" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-neutral-900">
                              {document.originalFilename}
                            </span>
                            {isLegacyDocument(document) && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                                <Lock className="h-3 w-3" />
                                {t(
                                  'vault.migrate.legacyBadge',
                                  'Chiffrement legacy'
                                )}
                              </span>
                            )}
                            {isServerSide &&
                              document.type === DocumentType.PAYSLIP &&
                              !isLegacyDocument(document) &&
                              (document.transferredToEmployeeVault ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                  <CheckCircle2 className="h-3 w-3" />
                                  {t(
                                    'vault.transfer.transferred',
                                    'Transféré au salarié'
                                  )}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                                  <Send className="h-3 w-3" />
                                  {t(
                                    'vault.transfer.pending',
                                    'En attente de transfert'
                                  )}
                                </span>
                              ))}
                          </div>
                          {document.period && (
                            <div className="text-sm text-neutral-500">
                              {document.period.month}/{document.period.year}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-center text-xs font-medium ${getDocumentTypeColor(document.type)}`}
                      >
                        {getDocumentTypeLabel(document.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600">
                      {formatFileSize(document.originalSize)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-neutral-600">
                        <Calendar className="h-4 w-4" />
                        {formatDate(document.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isLegacyDocument(document) ? (
                          <button
                            onClick={() => handleMigrateSingle(document)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100"
                            title={t(
                              'vault.migrate.makeAvailable',
                              'Rendre disponible'
                            )}
                          >
                            <RefreshCw className="h-4 w-4" />
                            {t(
                              'vault.migrate.makeAvailable',
                              'Rendre disponible'
                            )}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handlePreview(document)}
                              disabled={!isServerSide && !isUnlocked}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
                              title={t('common.preview', 'Visualiser')}
                            >
                              <Eye className="h-4 w-4" />
                              {t('common.preview', 'Visualiser')}
                            </button>
                            <button
                              onClick={() => handleDownload(document)}
                              disabled={!isServerSide && !isUnlocked}
                              className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                              title={t('common.download', 'Télécharger')}
                            >
                              <Download className="h-4 w-4" />
                              {t('common.download', 'Télécharger')}
                            </button>
                            {isServerSide &&
                              document.type === DocumentType.PAYSLIP &&
                              !document.transferredToEmployeeVault && (
                                <button
                                  onClick={() =>
                                    handleTransferToEmployee(document.uuid)
                                  }
                                  disabled={transferringDocId === document.uuid}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  title={t(
                                    'vault.transfer.toEmployee',
                                    'Transférer au salarié'
                                  )}
                                >
                                  {transferringDocId === document.uuid ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Send className="h-4 w-4" />
                                  )}
                                  {t(
                                    'vault.transfer.toEmployee',
                                    'Transférer au salarié'
                                  )}
                                </button>
                              )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-neutral-200 bg-white p-12 text-center">
          <FileText className="mx-auto h-12 w-12 text-neutral-400" />
          <h3 className="mt-4 text-lg font-semibold text-neutral-900">
            {searchQuery || periodFilter !== 'all'
              ? t('vault.empty.noDocuments', 'Aucun document trouvé')
              : t('vault.empty.noDocumentsYet', 'Aucun document')}
          </h3>
          <p className="mt-2 text-sm text-neutral-600">
            {searchQuery || periodFilter !== 'all'
              ? t(
                  'vault.empty.tryDifferentFilters',
                  'Essayez de modifier vos critères de recherche'
                )
              : t(
                  'vault.empty.uploadFirst',
                  'Commencez par téléverser votre premier document'
                )}
          </p>
        </div>
      )}

      {/* Migrate Document Modal */}
      <MigrateDocumentModal
        vaultId={vaultId}
        documents={documentsToMigrate}
        isOpen={migrateModalOpen}
        onClose={() => {
          setMigrateModalOpen(false);
          setDocumentsToMigrate([]);
        }}
        vaultData={{
          vaultKeySalt: vaultData?.vaultKeySalt,
          encryptedVaultKey: vaultData?.encryptedVaultKey,
          encryptedPrivateKey: vaultData?.encryptedPrivateKey,
          publicKey: vaultData?.publicKey,
        }}
      />

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative flex h-[90vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-neutral-500" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  {previewDocument?.originalFilename}
                </h3>
              </div>
              <button
                onClick={closePreview}
                className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden bg-neutral-100">
              {previewLoading ? (
                <div className="flex h-full items-center justify-center">
                  <div className="border-primary-600 h-10 w-10 animate-spin rounded-full border-4 border-t-transparent"></div>
                </div>
              ) : previewContent ? (
                <div className="h-full w-full">
                  {previewDocument?.mimeType?.startsWith('image/') ? (
                    <div className="flex h-full items-center justify-center p-4">
                      <img
                        src={previewContent}
                        alt={previewDocument?.originalFilename}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ) : previewDocument?.mimeType === 'application/pdf' ? (
                    <iframe
                      src={previewContent}
                      className="h-full w-full"
                      title={previewDocument?.originalFilename}
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
                      <FileText className="h-16 w-16 text-neutral-400" />
                      <p className="text-center text-neutral-600">
                        {t(
                          'vault.preview.unsupportedFormat',
                          'Ce type de fichier ne peut pas être prévisualisé.'
                        )}
                      </p>
                      <button
                        onClick={() =>
                          previewDocument && handleDownload(previewDocument)
                        }
                        className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        {t('common.download', 'Télécharger')}
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-neutral-200 px-6 py-4">
              <div className="text-sm text-neutral-500">
                {previewDocument && (
                  <>
                    {formatFileSize(previewDocument.originalSize)} •{' '}
                    {previewDocument.mimeType}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={closePreview}
                  className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                >
                  {t('common.close', 'Fermer')}
                </button>
                <button
                  onClick={() =>
                    previewDocument && handleDownload(previewDocument)
                  }
                  className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
                >
                  <Download className="h-4 w-4" />
                  {t('common.download', 'Télécharger')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
